/** @fileoverview Renders a Fabrik app definition with screen and sidebar navigation. */

import { Component, useCallback, useMemo, useRef, useState } from 'react';

import {
  AppSignedOutError,
  queryAppDataSource,
  readAppFieldOptions,
  readAppFieldValues,
  sendAppRuntimeRequest,
} from '../appMode/appRuntimeApi';
import { makeComponentStateApi } from '../services/componentStateApi';
import type { ComponentStateApi } from '../services/componentStateApi';
import {
  queryFabrikDataSource,
  readFabrikFieldOptions,
  readFabrikFieldValues,
} from '../services/fabrikApi';
import { getAppRuntimeBasePath, getBuilderRuntimeBasePath } from '../services/fabrikQueryPaths';
import { request } from '../services/platformAuth';
import { readRuntimeResponse } from '../services/runtimeResponse';
import { Button } from './ui/button';

import type { AppActionWrite, FabrikAction, FabrikVariable } from '../types/apps';
import { parseDefinition } from './AppBuilder/definition';
import { runLimited } from '../lib/requestLimit';
import { stableStringify } from '../lib/utils';
import { AppRuntimeProvider } from './AppRenderer/appRuntimeContext';
import {
  createFieldOptionsReader,
  createFieldValuesReader,
  useDataSourceLabels,
} from './AppRenderer/fieldOptions';
import { createFabrikGlobals } from './AppRenderer/globals';
import type { DataReader } from './AppRenderer/appRuntimeContext';
import {
  ComponentStateStoreProvider,
  createComponentStateStore,
} from './AppRenderer/componentStateStore';
import FabrikTheme from './AppRenderer/FabrikTheme';
import ScreenRenderer from './AppRenderer/ScreenRenderer';
import type { FabrikAppRendererProps } from './AppRenderer/types';
import { FabrikTenantProvider } from './AppRenderer/tenantContext';
import { useScreenNavigation } from './AppRenderer/useScreenNavigation';
import { readBlockedActionIds, readViewerGroups } from './AppRenderer/userGroups';

export { paletteStyle } from './AppRenderer/theme';

const NO_ACTIONS: FabrikAction[] = [];
const NO_VARIABLES: FabrikVariable[] = [];

/**
 * Runs a hosted call and tells the page when the app pass is no longer accepted.
 * @param call the hosted call to run
 * @param onSignedOut called when the call fails because the pass ended
 */
const withSignOut = async <Result,>(
  call: () => Promise<Result>,
  onSignedOut?: () => void
): Promise<Result> => {
  try {
    return await call();
  } catch (error) {
    if (error instanceof AppSignedOutError) {
      onSignedOut?.();
    }
    throw error;
  }
};

/**
 * Renders the app; in the builder an `appId` is needed for checklist, timeline, and action blocks.
 * @param props definition, tenant, mode, sign-out callback, and the optional app id
 */
function FabrikAppRendererInner({
  definition,
  tenantId,
  mode = 'builder',
  onSignedOut,
  appId,
  viewer,
}: FabrikAppRendererProps) {
  const viewerDetails =
    viewer && 'tenants' in viewer
      ? { firstName: viewer.first_name, lastName: viewer.last_name, email: viewer.email }
      : viewer;
  const firstName = viewerDetails?.firstName;
  const lastName = viewerDetails?.lastName;
  const email = viewerDetails?.email;
  const globals = useMemo(
    () => createFabrikGlobals({ firstName, lastName, email }),
    [email, firstName, lastName]
  );

  /* Variables stay out of this memo, so editing one does not recreate the rows store. */
  const runtime = useMemo(() => {
    if (mode === 'hosted') {
      const readData: DataReader = ({ dataSource, options, signal }) =>
        runLimited(
          () =>
            withSignOut(
              () =>
                queryAppDataSource({ systemId: dataSource.systemId, dataSource, options, signal }),
              onSignedOut
            ),
          signal
        );
      const componentState = makeComponentStateApi({
        basePath: getAppRuntimeBasePath(),
        send: (request) =>
          runLimited(
            () => withSignOut(() => sendAppRuntimeRequest(request), onSignedOut),
            request.signal
          ),
        globals,
      });
      const readFieldOptions = createFieldOptionsReader((request) =>
        runLimited(() => withSignOut(() => readAppFieldOptions(request), onSignedOut))
      );
      const readFieldValues = createFieldValuesReader((request) =>
        runLimited(() => withSignOut(() => readAppFieldValues(request), onSignedOut))
      );
      return { mode, readData, readFieldOptions, readFieldValues, componentState };
    }

    const readData: DataReader = ({ dataSource, options, signal }) =>
      runLimited(
        () => queryFabrikDataSource(tenantId, dataSource.systemId, dataSource, options, signal),
        signal
      );
    const componentState: ComponentStateApi | null = appId
      ? makeComponentStateApi({
          basePath: getBuilderRuntimeBasePath(appId),
          send: ({ path, method, body, signal }) =>
            runLimited(
              async () =>
                readRuntimeResponse(
                  await request(path, tenantId, { method, body: JSON.stringify(body), signal })
                ),
              signal
            ),
          globals,
        })
      : null;
    const readFieldOptions = createFieldOptionsReader((request) =>
      runLimited(() => readFabrikFieldOptions(tenantId, request))
    );
    const readFieldValues = createFieldValuesReader((request) =>
      runLimited(() => readFabrikFieldValues(tenantId, request))
    );
    return { mode, readData, readFieldOptions, readFieldValues, componentState };
  }, [appId, globals, mode, onSignedOut, tenantId]);

  const variables = definition.variables ?? NO_VARIABLES;
  const [dataVersion, setDataVersion] = useState(0);
  const reloadData = useCallback(() => setDataVersion((version) => version + 1), []);
  const labelOf = useDataSourceLabels(
    definition,
    runtime.readFieldOptions,
    runtime.readFieldValues
  );
  /* Navigation is made below; a stable callback reaches its latest state. */
  const applyWritesRef = useRef<(writes: AppActionWrite[]) => void>(() => undefined);
  const applyWrites = useCallback((writes: AppActionWrite[]) => applyWritesRef.current(writes), []);
  const runtimeValue = useMemo(
    () => ({ ...runtime, globals, variables, dataVersion, reloadData, labelOf, applyWrites }),
    [applyWrites, dataVersion, globals, labelOf, reloadData, runtime, variables]
  );
  const { componentState } = runtime;
  const store = useMemo(
    () => (componentState ? createComponentStateStore(componentState) : null),
    [componentState]
  );
  const actions = definition.actions ?? NO_ACTIONS;
  /*
   * A hosted app only knows the groups the server sent, since its copy has no emails; the
   * builder holds the emails and works the groups out itself.
   */
  const serverGroups =
    viewerDetails && 'groups' in viewerDetails ? viewerDetails.groups : undefined;
  const { userGroups } = definition;
  const blockedActionIds = useMemo(
    () =>
      readBlockedActionIds(
        actions,
        mode === 'hosted' ? serverGroups ?? [] : readViewerGroups(userGroups, email)
      ),
    [actions, email, mode, serverGroups, userGroups]
  );
  const storeValue = useMemo(
    () => ({ store, actions, blockedActionIds }),
    [actions, blockedActionIds, store]
  );

  const navigation = useScreenNavigation(definition);
  applyWritesRef.current = navigation.applyWrites;
  const hasSidebar = Boolean(navigation.screenBlocks[navigation.screenBlocks.length - 1]?.sidebar);

  return (
    <FabrikTheme className="min-h-0 min-w-0 bg-background text-foreground" theme={definition.theme}>
      <AppRuntimeProvider value={runtimeValue}>
        <ComponentStateStoreProvider value={storeValue}>
          <FabrikTenantProvider value={tenantId}>
            <main
              className={`w-full min-w-0 px-6 py-12 pb-20 lg:px-10 ${
                hasSidebar ? 'lg:mr-96 lg:w-auto' : ''
              }`}
            >
              {/* Content stops at a readable width on wide screens. */}
              <div className="mx-auto w-full max-w-300">
                <ScreenRenderer definition={definition} navigation={navigation} />
              </div>
            </main>
            <footer className="fixed bottom-3 right-4 z-50 flex gap-1 rounded-full border border-app-brand/30 bg-app-brand-subtle px-3 py-1 text-xs text-app-text-secondary shadow-sm">
              Powered by{' '}
              <strong className="font-semibold text-app-brand-subtle-foreground">
                INTEGRTR.Fabrik
              </strong>
            </footer>
          </FabrikTenantProvider>
        </ComponentStateStoreProvider>
      </AppRuntimeProvider>
    </FabrikTheme>
  );
}

/** A definition cannot be rendered without risking missing or misleading app content. */
export class UnsupportedDefinitionError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'UnsupportedDefinitionError';
  }
}

/** Generic fallback shown under the app navigation; never expose definition or runtime details. */
export function UnsupportedAppDefinition() {
  return (
    <section role="alert" className="mx-auto w-full max-w-5xl space-y-4 px-7 py-12 text-foreground">
      <h1 className="text-xl font-semibold">This app cannot be displayed</h1>
      <p className="text-sm text-muted-foreground">
        This app is invalid or unsupported for this UI version.
      </p>
      <Button type="button" variant="secondary" onClick={() => window.location.reload()}>
        Reload
      </Button>
    </section>
  );
}

class RendererErrorBoundary extends Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  render() {
    return this.state.hasError ? <UnsupportedAppDefinition /> : this.props.children;
  }
}

const COMPONENT_KINDS = new Set([
  'text',
  'button',
  'list',
  'checklist',
  'timeline',
  'fields',
  'input',
  'form',
  'grid',
  'card',
  'metric',
  'icon',
  'chart',
]);

/** Finds unsupported discriminators before mounting any renderer hooks. */
const readUnknownKind = (definition: unknown): string | undefined => {
  if (
    !definition ||
    typeof definition !== 'object' ||
    !('screens' in definition) ||
    !Array.isArray(definition.screens)
  )
    return undefined;
  for (const [screenIndex, screen] of definition.screens.entries()) {
    if (
      !screen ||
      typeof screen !== 'object' ||
      !('components' in screen) ||
      !Array.isArray(screen.components)
    )
      continue;
    const inspect = (
      components: unknown[],
      path: string,
      parentLayoutPath: ('grid' | 'card')[] = []
    ): string | undefined => {
      for (const [index, component] of components.entries()) {
        const location = `${path}[${index}]`;
        if (
          !component ||
          typeof component !== 'object' ||
          !('kind' in component) ||
          typeof component.kind !== 'string'
        )
          return `Unsupported component discriminator at ${location}.`;
        if (!COMPONENT_KINDS.has(component.kind))
          return `Unknown component kind "${component.kind}" at ${location}.kind.`;
        const layoutKind: 'grid' | 'card' | undefined =
          component.kind === 'grid' ? 'grid' : component.kind === 'card' ? 'card' : undefined;
        const layoutPath = layoutKind ? [...parentLayoutPath, layoutKind] : parentLayoutPath;
        const validLayoutPath =
          !layoutKind ||
          layoutPath.length === 1 ||
          (layoutPath[0] === 'grid' &&
            layoutPath[1] === 'card' &&
            (layoutPath.length === 2 || (layoutPath.length === 3 && layoutPath[2] === 'grid'))) ||
          (layoutPath.length === 2 && layoutPath[0] === 'card' && layoutPath[1] === 'grid');
        if (!validLayoutPath)
          return `Grid/Card nesting only supports Grid → Card → Grid or Card → Grid at ${location}.`;
        if (component.kind === 'grid' && 'items' in component && Array.isArray(component.items)) {
          for (const [itemIndex, item] of component.items.entries()) {
            if (!item || typeof item !== 'object' || !('component' in item))
              return `Invalid grid item at ${location}.items[${itemIndex}].`;
            const issue = inspect(
              [item.component],
              `${location}.items[${itemIndex}].component`,
              layoutPath
            );
            if (issue) return issue;
          }
        }
        if (component.kind === 'card' && 'content' in component) {
          const issue = inspect([component.content], `${location}.content`, layoutPath);
          if (issue) return issue;
        }
        if (component.kind === 'form' && 'inputs' in component && Array.isArray(component.inputs)) {
          for (const [inputIndex, input] of component.inputs.entries()) {
            if (!input || typeof input !== 'object' || !('kind' in input) || input.kind !== 'input')
              return `Unknown input discriminator at ${location}.inputs[${inputIndex}].kind.`;
          }
        }
      }
      return undefined;
    };
    const issue = inspect(screen.components, `screens[${screenIndex}].components`);
    if (issue) return issue;
  }
  return undefined;
};

export default function FabrikAppRenderer(props: FabrikAppRendererProps) {
  try {
    const unknownKind = readUnknownKind(props.definition);
    if (unknownKind) throw new UnsupportedDefinitionError(unknownKind);
    const serialized = JSON.stringify(props.definition);
    if (typeof serialized !== 'string')
      throw new UnsupportedDefinitionError('Definition is not JSON data.');
    const result = parseDefinition(serialized, { emailsHidden: props.mode === 'hosted' });
    if (result.error) throw new UnsupportedDefinitionError(result.error);
  } catch {
    return <UnsupportedAppDefinition />;
  }
  const resetKey = `${props.definitionVersion ?? ''}:${stableStringify(props.definition)}`;
  return (
    <RendererErrorBoundary key={resetKey}>
      <FabrikAppRendererInner {...props} />
    </RendererErrorBoundary>
  );
}
